import React, { useEffect, useState } from 'react'
import { TextField } from '@entur/form'
import { format, isValid, parse } from 'date-fns'
import { ValidationFormField, validateFormField } from '../../utils/form-utils'
import { toPrintFormat } from '../../utils/date-utils'

const formatDate = (value: Date) => format(value, 'dd.MM.yyyy')

const parseDate = (value: string | null) => {
  if (value == null || value.trim().length === 0) return null

  const possibleFormats = ['dd.MM.yyyy', 'ddMMyyyy', 'dd.MM.yy', 'ddMMyy']

  return (
    possibleFormats
      .map((format) => {
        const parsed = parse(value.trim(), format, new Date())

        // Only count as valid date if string value is as long as the format we are matching
        // this is because date-fns parse will happily parse 20.09.95 as being a valid date in
        // the year 95. Since this is probably not what you want, instead count it as invalid
        if (isValid(parsed) && value.trim().length >= format.length) return parsed
        else return null
      })
      .find((date) => date != null) ?? undefined
  )
}

export const datePickerFormField = (
  fieldName: string,
  displayName: string,
  value: Date | undefined,
  required = false,
  draftValue: Date | undefined = undefined,
  validateFuture = false
): ValidationFormField<Date | undefined> => {
  return {
    value: value ? new Date(value) : undefined,
    displayValue: value ? toPrintFormat(value) : '',
    draftValue: draftValue ? toPrintFormat(draftValue) : undefined,
    variant: '',
    feedback: '',
    fieldName,
    displayName,
    validation: {
      required,
      fun: (date: unknown) => {
        if (validateFuture && (date as Date) > new Date()) {
          return {
            valid: false,
            message: 'Denne datoen kan ikke være etter dagens dato',
          }
        }
        return { valid: true }
      },
    },
  }
}

type ValidatableDatePickerProps = {
  date: ValidationFormField<Date | undefined>
  update: (value: ValidationFormField<Date | undefined>) => void
}
/**
 *
 * This datepicker is just a regular text field where the date must be written in the correct format.
 * This was the behavior of the old datepicker in the design system but later it was rewritten to be much more
 * "integrated" with mutltiple input fields for choosing parts of the date and a calendar picker.
 * But, the regular old text field worked just find for the purposes used in this application, so just keep on doing that
 * instead of using the new datepicker :)
 */
const ValidatableDatePicker: React.FC<ValidatableDatePickerProps> = ({ date, update }) => {
  const [stringValue, setStringValue] = useState(date.value ? formatDate(date.value) : '')

  useEffect(() => {
    // When we get a new value from outside, only update internal state if the new value is valid.
    if (date.variant !== 'error') {
      setStringValue(date.value ? formatDate(date.value) : '')
    }
  }, [date.value, date.variant])

  const validateAndUpdate = () => {
    const parsedDate = parseDate(stringValue)

    if (parsedDate !== undefined) {
      parsedDate?.setHours(12, 0, 0, 0)
      const changedDateToValidate = {
        ...date,
        value: parsedDate || undefined,
        displayValue: parsedDate ? toPrintFormat(parsedDate) : '',
      }
      const validated = validateFormField(changedDateToValidate || date) as ValidationFormField<Date | undefined>
      setStringValue(parsedDate ? formatDate(parsedDate) : '')
      update(validated)
    } else {
      update({
        ...date,
        value: undefined,
        displayValue: '',
        variant: 'error',
        feedback: 'Ugyldig dato',
      })
    }
  }

  return (
    <TextField
      data-cy={date.fieldName}
      name={date.fieldName}
      label={date.displayName}
      variant={date.variant as 'success' | 'error' | 'warning' | 'info' | undefined}
      feedback={date.feedback}
      value={stringValue}
      onChange={(e) => setStringValue(e.currentTarget.value)}
      onBlur={() => validateAndUpdate()}
      onKeyDown={(e) => e.key === 'Enter' && validateAndUpdate()}
      onFocus={(e) => e.currentTarget.select()}
      placeholder="dd.mm.yyyy"
      style={{ cursor: 'pointer' }}
    />
  )
}

export default ValidatableDatePicker
